<template>
  <DefaultPageContainer
    :cnc-ads-page="cncAdsPage"
    :breadcrumb-text="section.name"
  >

    <!-- TITLE -->
    <section class="section">
      <div class="section__head">
        <h1 class="h1">{{ section.name }}</h1>
      </div>
    </section>

    <section class="section">
      <!-- ARTICLES -->
      <DefaultTwoColumnsContent
        right-container-class="section__right-col--mobile-hide"
      >
        <template #left>
          <!-- NO ARTICLES -->
          <DefaultErrorsNoResultsFound
            v-if="!articleStore.getFilteredArticlesCount() && !articleStore.isLoading"
          />

          <div class="section__articles-container">
            <!-- article list -->
            <DefaultArticlesListing
              :articles="articleStore.getOnlyFilteredArticles(0, (page * pageArticlesCount))"
              :ad-page="cncAdsPage"
              :firstArticleType="ArticleComponentType.BIG"
            />

            <!-- Loading NEXT page -->
            <div
              v-if="articleStore.isLoading"
              v-for="item in 10"
              :key="`skeleton_${item}`"
              class="article-wrapper"
            >
              <DefaultSkeletonsArticleCard
                :type="item === 1 && !articleStore.getFilteredArticlesCount() ? ArticleComponentType.BIG : ArticleComponentType.DEFAULT"
              />
            </div>

            <!-- Next page -->
            <div
              v-if="articleStore.getArticlesCount() >= articleStore.limit"
              class="button-more-container"
            >
              <DefaultArticlesButtonMore
                :label="$t('articles.button_more')"
                :disabled="articleStore.isLoading"
                @click="nextPage"
              />
            </div>

            <!-- External articles (články z jiných titulů) -->
            <DefaultExternalArticles
              :title="$t('articles.external_articles_title')"
              :articles-count="pageRecommendArticlesCount"
            />
          </div>
        </template>
        <template #right>
          <!-- CNC Ads -->
          <div class="advertisement-right-col h-full">
            <div class="sticky-sidebar-ad-wrapper">
              <DefaultAdsCncWrapper
                :position="UniFeAdsPosition.COLUMN_STICKY"
                :page="cncAdsPage"
              />
            </div>
          </div>
        </template>
      </DefaultTwoColumnsContent>
    </section>
  </DefaultPageContainer>
</template>

<script setup lang="ts">
import {ARTICLE_QUERY_TYPE, useArticlesStore} from "~/stores/useArticlesStore"
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import ArticleComponentType from "~/libs/constants/ArticleComponentType"
import {useCncAdsWrapper} from "~/composables/useCncAdsWrapper"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import {useSeoWrapper} from "~/composables/useSeoWrapper"
import {MenuHeaderConfig} from "~/config/MenuHeaderConfig"

// i18n routing
definePageMeta({
  name: 'sectionUrl',
})

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const cncAdsPage = CncAdsPage.SECTION
const staticMenuConfig = useStaticMenuConfig(MenuHeaderConfig)

const section = staticMenuConfig.getMenuItemByUrl(route.params.sectionUrl as string)

if (!section) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

// Set SEO meta
useSeoWrapper().setSeoMeta({
  title: section.name,
})
const filters = staticMenuConfig.getMenuItemsFilters([section])

const articleStore = useArticlesStore(ArticleStoreType.SECTION, section.url)
const pageArticlesCount = Number(config.public.sectionPageArticlesCount || 17)
const pageSsrArticlesCount = Number(config.public.sectionSsrArticlesCount || 6)
let page = ref(Number(route.query.page || 1))

/**
 * Recommended articles
 */
const pageRecommendArticlesCount = Number(config.public.sectionRecommendArticlesCount || 5)

/**
 * Load next page - articles
 */
const nextPage = async () => {
  page.value = page.value + 1
  router.push({
    query: { page: page.value },
  })
  try {
    articleStore.setLimit(page.value * pageArticlesCount)
    articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW).then(() => {
      articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
    })
  } catch (e) {
    // TODO - show message to customer?
  }
}

if (import.meta.server) {
  // sever articles preload - preload pageSsrArticlesCount articles on server
  try {
    articleStore.setLimit(pageSsrArticlesCount)
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW)
  } catch (e) {
    // TODO - show message to customer?
  }
}

/**
 * On mounted - load data
 */
onMounted(async () => {
  // client side data load
  try {
    articleStore.setLimit(page.value * pageArticlesCount)
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW)
    await articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
  } catch (e) {
    // TODO - show message to customer?
  }

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })
})
</script>
